import { useCallback, useEffect, useMemo, useState } from 'react';
import AdminHeader from '../../components/AdminHeader';
import { PeriodSelector } from '../../components/AdminPeriodSelector';
import { useAdminPeriod } from '../../components/useAdminPeriod';
import { formatDuracaoDias, tempoMedioEntreComprasDias, ticketMedioComprador } from './funil-recompra.mjs';
import PriceTestOfferRevenue, { type PriceTestRevenueSummary } from './PriceTestOfferRevenue';

type FunilEstagio = { estagio: string; conversas: number; pct_do_topo: number; avg_msgs: number };
type EstadoRow = {
  uf: string; conversas_unicas: number; clientes_pagantes: number;
  pedidos_pagos: number; pct_conv: number | null; receita: number;
};
type EstiloRow = {
  estilo_grupo: string; pedidos: number; pagos: number;
  pct_conv: number; ticket_medio: number; amostra_pequena: boolean;
};
type RelacaoRow = {
  relacao_grupo: string; pedidos: number; pagos: number;
  pct_conv: number; ticket_medio: number; amostra_pequena: boolean;
};
type CruzadaRow = {
  estilo: string; relacao: string; pedidos: number; pagos: number; pct_conv: number;
};
type ObjecaoRow = { categoria: string; sessoes: number; exemplos: string[] };
type RecompraData = {
  pct_recompra: number;
  total_clientes_pagantes: number;
  clientes_recorrentes: {
    cliente_phone: string; cliente_nome: string | null; ddd: string;
    pedidos_pagos: number; receita_total: number;
    primeira_compra: string; ultima_compra: string; dias_entre_compras: number;
  }[];
};
type FunilData = {
  period_days: number;
  total_conversas: number;
  pagos: number;
  conversao_total_pct: number;
  vendas_periodo_total: number;
  vendas_periodo_clientes_unicos: number;
  vendas_funil_completo_no_periodo: number;
  funil_estagios: FunilEstagio[];
  conversao_estado: EstadoRow[];
  conversao_estilo: EstiloRow[];
  conversao_relacao: RelacaoRow[];
  cruzada_estilo_relacao: CruzadaRow[];
  objecoes: ObjecaoRow[];
  recompra: RecompraData;
  price_test?: PriceTestRevenueSummary | null;
};

const ESTAGIO_LABELS: Record<string, string> = {
  '1_clicou': 'Só clicou no anúncio',
  '2_engajou': 'Engajou',
  '3_previas': 'Prévias recebidas',
  '4_pago': 'Pedido pago',
};

// Cor por etapa do funil (peach → dourado → âmbar → verde no "pago")
const ESTAGIO_CORES: Record<string, string> = {
  '1_clicou': '#ffc59e',
  '2_engajou': '#ff914d',
  '3_previas': '#e07a38',
  '4_pago': '#2f7a4a',
};

const OBJECAO_LABELS: Record<string, string> = {
  preço_alto: 'Preço alto',
  pediu_desconto: 'Pediu desconto',
  preço_anuncio_diferente: 'Preço do anúncio diferente',
  pediu_boleto: 'Pediu boleto',
  problema_pix: 'Problema com PIX',
  adiamento: 'Adiamento',
  pediu_exemplo: 'Pediu exemplo',
  fraude_golpe: 'Suspeita de golpe',
};

function formatBRL(v: number | null | undefined): string {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function formatInt(v: number | null | undefined): string {
  return String(v ?? 0);
}
function formatPct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${Number(v).toFixed(1)}%`;
}

export default function Funil() {
  const { apiQuery } = useAdminPeriod();

  const [data, setData] = useState<FunilData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await fetch(`/api/admin-dashboard?section=funil&${apiQuery}`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const j = await res.json();
      setData(j);
    } catch (err) {
      setError(`Falha ao carregar: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, [apiQuery]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  return (
    <div
      className="admin-shell"
      style={{
        minHeight: '100vh',
        background: 'var(--cream)',
        color: 'var(--text-body)',
        fontFamily: "'DM Sans', sans-serif",
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <AdminHeader title="Funil" />

      <main
        style={{
          flex: 1,
          padding: '20px 16px 60px',
          maxWidth: 1280,
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
            marginBottom: 20,
            flexWrap: 'wrap',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 24,
              fontWeight: 800,
              letterSpacing: '-0.03em',
              color: 'var(--heading-ink)',
            }}
          >
            Análise de funil
          </h1>
          <PeriodSelector />
        </div>

        {loading && !data && <SkeletonGrid />}

        {error && (
          <div
            style={{
              padding: 20,
              borderRadius: 12,
              background: 'rgba(220,80,80,0.08)',
              border: '1px solid rgba(220,80,80,0.30)',
              color: '#a93232',
              textAlign: 'center',
              marginBottom: 20,
            }}
          >
            <p style={{ margin: '0 0 12px', fontSize: 14 }}>{error}</p>
            <button
              onClick={() => {
                setLoading(true);
                load();
              }}
              style={{
                padding: '8px 16px',
                borderRadius: 999,
                border: 'none',
                background: 'var(--gold)',
                color: 'var(--gold-ink)',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              Tentar novamente
            </button>
          </div>
        )}

        {data && (
          <>
            <FunilEstagios data={data} />
            <PriceTestOfferRevenue
              data={data.price_test}
              note="Receita da música em cada workflow. O upsell de vídeo e os estágios de conversão V16 acima não entram neste recorte."
            />
            <ConversaoEstado rows={data.conversao_estado} />
            <ConversaoEstilo rows={data.conversao_estilo} />
            <ConversaoRelacao rows={data.conversao_relacao} />
            <HeatmapEstiloRelacao rows={data.cruzada_estilo_relacao} />
            <Objecoes rows={data.objecoes} />
            <Recompra data={data.recompra} />
          </>
        )}
      </main>
    </div>
  );
}

// ============ 1) FUNIL DE ESTÁGIOS ============

function FunilEstagios({ data }: { data: FunilData }) {
  const max = Math.max(1, ...data.funil_estagios.map((s) => s.conversas));
  return (
    <section style={{ marginTop: 8 }}>
      <SectionTitle>Funil de conversão</SectionTitle>
      <Card>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 12,
            marginBottom: 20,
          }}
        >
          <KpiCard label="Conversas no período" value={formatInt(data.total_conversas)} />
          <KpiCard
            label="Conversão funil"
            value={formatPct(data.conversao_total_pct)}
            hint="funil completo / conversas"
            accent="green"
          />
          <KpiCard
            label="Vendas (total)"
            value={formatInt(data.vendas_periodo_total)}
            hint="pedidos pagos no período"
            tooltip="Todos os pedidos pagos no período. Conta recompra (cliente que paga 2× = 2 vendas)."
            accent="green"
          />
          <KpiCard
            label="Vendas (clientes únicos)"
            value={formatInt(data.vendas_periodo_clientes_unicos)}
            hint="phones únicos pagantes"
            tooltip="Clientes únicos que pagaram no período (deduplicado por telefone). Recompra conta como 1 cliente."
            accent="green"
          />
          <KpiCard
            label="Funil completo"
            value={formatInt(data.vendas_funil_completo_no_periodo)}
            hint="conversaram E pagaram"
            tooltip="Clientes que tanto iniciaram conversa quanto pagaram dentro do período. Quem chatou antes do cutoff e pagou no período não conta aqui."
          />
        </div>

        {data.funil_estagios.length === 0 ? (
          <EmptyMsg>Sem conversas no período.</EmptyMsg>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 200px) 1fr minmax(78px, 110px)', alignItems: 'stretch', rowGap: 0 }}>
            {data.funil_estagios.map((s, i) => {
              const arr = data.funil_estagios;
              const wTop = Math.max(0.04, s.conversas / max);
              const next = arr[i + 1];
              // último segmento fecha reto na própria largura; senão afunila até a próxima etapa
              const wBot = next ? Math.max(0.04, next.conversas / max) : wTop;
              const cor = ESTAGIO_CORES[s.estagio] || 'var(--gold)';
              const pct = (x: number) => +(x * 100).toFixed(2);
              const clip = `polygon(${pct((1 - wTop) / 2)}% 0, ${pct((1 + wTop) / 2)}% 0, ${pct((1 + wBot) / 2)}% 100%, ${pct((1 - wBot) / 2)}% 100%)`;
              const prev = arr[i - 1];
              const stepConv = prev && prev.conversas > 0 ? (s.conversas / prev.conversas) * 100 : null;
              return (
                <div key={s.estagio} style={{ display: 'contents' }}>
                  {/* coluna esquerda: rótulo */}
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', paddingRight: 10 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--heading-ink)', lineHeight: 1.2 }}>
                      {ESTAGIO_LABELS[s.estagio] || s.estagio}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 400 }}>
                      média de {s.avg_msgs} msgs do cliente
                    </div>
                    {stepConv != null && (
                      <div style={{ fontSize: 10, color: 'var(--text-soft)', fontWeight: 600, marginTop: 2 }}>
                        ↓ {stepConv.toFixed(1)}% da etapa anterior
                      </div>
                    )}
                  </div>

                  {/* coluna central: segmento do funil (trapézio) */}
                  <div style={{ position: 'relative', height: 72 }}>
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        clipPath: clip,
                        WebkitClipPath: clip,
                        background: cor,
                        transition: 'clip-path 0.4s',
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#fff',
                        fontSize: 18,
                        fontWeight: 800,
                        letterSpacing: '-0.02em',
                        textShadow: '0 1px 3px rgba(0,0,0,0.35)',
                        pointerEvents: 'none',
                      }}
                    >
                      {s.conversas}
                    </div>
                  </div>

                  {/* coluna direita: % do topo */}
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end', paddingLeft: 8 }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: cor === '#ffc59e' ? 'var(--amber-deep)' : cor, letterSpacing: '-0.02em' }}>
                      {s.pct_do_topo}%
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>do topo</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </section>
  );
}

// ============ 2) CONVERSÃO POR ESTADO ============

function ConversaoEstado({ rows }: { rows: EstadoRow[] }) {
  type SortKey = 'uf' | 'conversas_unicas' | 'clientes_pagantes' | 'pedidos_pagos' | 'pct_conv' | 'receita';
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'receita', dir: 'desc' });

  const sorted = useMemo(() => {
    const c = [...rows];
    c.sort((a, b) => {
      const va = a[sort.key] as number | string | null;
      const vb = b[sort.key] as number | string | null;
      const an = va == null ? -Infinity : typeof va === 'number' ? va : String(va).localeCompare(String(vb));
      const bn = vb == null ? -Infinity : typeof vb === 'number' ? vb : 0;
      if (typeof va === 'string') return sort.dir === 'asc' ? (an as number) : -(an as number);
      const diff = (an as number) - (bn as number);
      return sort.dir === 'asc' ? diff : -diff;
    });
    return c;
  }, [rows, sort]);

  const toggle = (k: SortKey) => {
    setSort((s) => (s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'desc' }));
  };

  const rowColor = (pct: number | null): string => {
    if (pct == null) return 'transparent';
    if (pct >= 25) return 'rgba(120, 235, 130, 0.20)';
    if (pct <= 10) return 'rgba(230, 95, 95, 0.16)';
    return 'transparent';
  };

  return (
    <section style={{ marginTop: 32 }}>
      <SectionTitle>Conversão por estado</SectionTitle>
      <Card>
        {rows.length === 0 ? (
          <EmptyMsg>Sem dados no período.</EmptyMsg>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                  <Th onClick={() => toggle('uf')} active={sort.key === 'uf'} dir={sort.dir} align="left">UF</Th>
                  <Th onClick={() => toggle('conversas_unicas')} active={sort.key === 'conversas_unicas'} dir={sort.dir}>Conversas</Th>
                  <Th onClick={() => toggle('clientes_pagantes')} active={sort.key === 'clientes_pagantes'} dir={sort.dir}>Pagantes</Th>
                  <Th onClick={() => toggle('pedidos_pagos')} active={sort.key === 'pedidos_pagos'} dir={sort.dir}>Pedidos pagos</Th>
                  <Th onClick={() => toggle('pct_conv')} active={sort.key === 'pct_conv'} dir={sort.dir}>% conv</Th>
                  <Th onClick={() => toggle('receita')} active={sort.key === 'receita'} dir={sort.dir}>Receita</Th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr
                    key={r.uf}
                    style={{
                      borderTop: '1px solid var(--border-fade)',
                      background: rowColor(r.pct_conv),
                    }}
                  >
                    <td style={{ padding: '8px', fontWeight: 700, color: 'var(--heading-ink)' }}>{r.uf}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{r.conversas_unicas}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{r.clientes_pagantes}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{r.pedidos_pagos}</td>
                    <td style={{
                      padding: '8px', textAlign: 'right', fontWeight: 800,
                      color: r.pct_conv != null && r.pct_conv >= 25
                        ? 'var(--conv-good)'
                        : r.pct_conv != null && r.pct_conv <= 10
                          ? 'var(--conv-bad)'
                          : undefined,
                    }}>{formatPct(r.pct_conv)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{formatBRL(r.receita)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

// ============ 3) CONVERSÃO POR ESTILO ============

function ConversaoEstilo({ rows }: { rows: EstiloRow[] }) {
  return (
    <section style={{ marginTop: 32 }}>
      <SectionTitle>Conversão por estilo musical</SectionTitle>
      <Disclaimer>
        Esta análise considera apenas pedidos que chegaram a registrar um estilo (após briefing).
        Não reflete o funil completo desde a primeira conversa.
      </Disclaimer>
      <Card>
        {rows.length === 0 ? (
          <EmptyMsg>Sem pedidos com estilo no período.</EmptyMsg>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Estilo</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Pedidos</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Pagos</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>% conv</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Ticket médio</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.estilo_grupo} style={{ borderTop: '1px solid var(--border-fade)' }}>
                    <td style={{ padding: '8px', fontWeight: 700, color: 'var(--heading-ink)' }}>
                      {r.estilo_grupo}
                      {r.amostra_pequena && (
                        <span
                          title="Amostra pequena (<5 pedidos) — resultados podem ser ruidosos"
                          style={{ marginLeft: 6, color: 'var(--gold)' }}
                        >
                          ⚠️
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{r.pedidos}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{r.pagos}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{formatPct(r.pct_conv)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{formatBRL(r.ticket_medio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

// ============ 4) CONVERSÃO POR RELAÇÃO ============

function ConversaoRelacao({ rows }: { rows: RelacaoRow[] }) {
  return (
    <section style={{ marginTop: 32 }}>
      <SectionTitle>
        Conversão por relação (homenageado){' '}
        <span
          title="Relação significa 'o homenageado é meu O QUÊ', na perspectiva do comprador. Ex: relacao=filha → 'comprei pra minha filha' (a filha recebe a música). O sistema NÃO captura o gênero do comprador; parceiro amoroso é dividido só pelo gênero de quem recebe: parceira (esposa/namorada), parceiro (marido/namorado) e parceiro(a) s/ gênero (amor/parceiro)."
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            borderRadius: 999,
            background: 'var(--badge-bg)',
            color: 'var(--text-muted)',
            fontSize: 10,
            fontWeight: 700,
            cursor: 'help',
            verticalAlign: 'middle',
            marginLeft: 6,
          }}
        >
          ?
        </span>
      </SectionTitle>
      <Card>
        {rows.length === 0 ? (
          <EmptyMsg>Sem pedidos com relação no período.</EmptyMsg>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Relação</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Pedidos</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Pagos</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>% conv</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Ticket médio</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.relacao_grupo} style={{ borderTop: '1px solid var(--border-fade)' }}>
                    <td style={{ padding: '8px', fontWeight: 700, color: 'var(--heading-ink)' }}>
                      {r.relacao_grupo}
                      {r.amostra_pequena && (
                        <span
                          title="Amostra pequena (<5 pedidos)"
                          style={{ marginLeft: 6, color: 'var(--gold)' }}
                        >
                          ⚠️
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{r.pedidos}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{r.pagos}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{formatPct(r.pct_conv)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{formatBRL(r.ticket_medio)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

// ============ 5) HEATMAP ESTILO × RELAÇÃO ============

function HeatmapEstiloRelacao({ rows }: { rows: CruzadaRow[] }) {
  const { estilos, relacoes, cell } = useMemo(() => {
    const estilos = Array.from(new Set(rows.map((r) => r.estilo))).sort();
    const relacoes = Array.from(new Set(rows.map((r) => r.relacao))).sort();
    const cell: Record<string, CruzadaRow> = {};
    for (const r of rows) cell[`${r.estilo}|${r.relacao}`] = r;
    return { estilos, relacoes, cell };
  }, [rows]);

  // Top 3 / bottom 3 com >=10 pedidos
  const { top3, bot3 } = useMemo(() => {
    const eligible = rows.filter((r) => r.pedidos >= 10);
    const sorted = [...eligible].sort((a, b) => b.pct_conv - a.pct_conv);
    return {
      top3: sorted.slice(0, 3),
      bot3: sorted.slice(-3).reverse(),
    };
  }, [rows]);

  const colorFor = (pct: number): string => {
    if (pct >= 50) return '#2f7a4a';
    if (pct >= 30) return '#9dd1a8';
    if (pct >= 15) return '#f7d486';
    return '#e89494';
  };

  return (
    <section style={{ marginTop: 32 }}>
      <SectionTitle>Heatmap estilo × relação</SectionTitle>

      {(top3.length > 0 || bot3.length > 0) && (
        <Callout>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {top3.length > 0 && (
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                  🏆 Top 3 (≥10 pedidos)
                </div>
                {top3.map((c, i) => (
                  <div key={i} style={{ fontSize: 13, color: 'var(--text-strong)' }}>
                    <b>{c.estilo} × {c.relacao}</b>: {c.pagos}/{c.pedidos} · {formatPct(c.pct_conv)}
                  </div>
                ))}
              </div>
            )}
            {bot3.length > 0 && (
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: 6 }}>
                  📉 Bottom 3 (≥10 pedidos)
                </div>
                {bot3.map((c, i) => (
                  <div key={i} style={{ fontSize: 13, color: 'var(--text-strong)' }}>
                    <b>{c.estilo} × {c.relacao}</b>: {c.pagos}/{c.pedidos} · {formatPct(c.pct_conv)}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Callout>
      )}

      <Card>
        {rows.length === 0 ? (
          <EmptyMsg>Sem células com ≥2 pedidos no período.</EmptyMsg>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 2, fontSize: 11 }}>
              <thead>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left', color: 'var(--text-muted)' }} />
                  {relacoes.map((r) => (
                    <th
                      key={r}
                      style={{
                        padding: '6px 4px',
                        textAlign: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--text-muted)',
                        minWidth: 80,
                      }}
                    >
                      {r}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {estilos.map((e) => (
                  <tr key={e}>
                    <td
                      style={{
                        padding: '6px 8px',
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'var(--heading-ink)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {e}
                    </td>
                    {relacoes.map((rel) => {
                      const c = cell[`${e}|${rel}`];
                      if (!c) {
                        return (
                          <td
                            key={rel}
                            style={{
                              padding: 6,
                              textAlign: 'center',
                              background: 'var(--badge-bg)',
                              color: 'var(--text-soft)',
                              borderRadius: 4,
                              fontSize: 11,
                            }}
                          >
                            —
                          </td>
                        );
                      }
                      const bg = colorFor(c.pct_conv);
                      return (
                        <td
                          key={rel}
                          title={`${c.estilo} × ${c.relacao}: ${c.pagos}/${c.pedidos} · ${c.pct_conv}%`}
                          style={{
                            padding: 6,
                            textAlign: 'center',
                            background: bg,
                            color: '#fff',
                            borderRadius: 4,
                            fontWeight: 600,
                            lineHeight: 1.2,
                          }}
                        >
                          <div style={{ fontSize: 11 }}>{c.pagos}/{c.pedidos}</div>
                          <div style={{ fontSize: 10, opacity: 0.95 }}>{c.pct_conv}%</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

// ============ 6) OBJEÇÕES ============

function Objecoes({ rows }: { rows: ObjecaoRow[] }) {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (cat: string) => setExpanded((e) => ({ ...e, [cat]: !e[cat] }));

  return (
    <section style={{ marginTop: 32 }}>
      <SectionTitle>Objeções detectadas em mensagens</SectionTitle>
      <Disclaimer>
        Contagem por sessão única. O texto interno injetado pela Isa (bloco{' '}
        <code style={{ fontSize: 11 }}>[CONTEXTO INTERNO ...]</code>) é truncado antes da análise.
      </Disclaimer>
      {rows.length === 0 ? (
        <Card><EmptyMsg>Sem objeções no período.</EmptyMsg></Card>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 12,
          }}
        >
          {rows.map((o) => {
            const open = expanded[o.categoria];
            return (
              <div
                key={o.categoria}
                className="admin-glass"
                style={{
                  background: 'var(--surface-white)',
                  borderRadius: 18,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: 'var(--heading-ink)',
                    }}
                  >
                    {OBJECAO_LABELS[o.categoria] || o.categoria}
                  </div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: 'var(--gold)',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {o.sessoes}
                  </div>
                </div>
                {o.exemplos.length > 0 && (
                  <button
                    onClick={() => toggle(o.categoria)}
                    style={{
                      alignSelf: 'flex-start',
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: 'var(--badge-bg)',
                      border: '1px solid var(--border-soft)',
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--text-strong)',
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                    }}
                  >
                    {open ? 'Esconder' : `Ver exemplos (${o.exemplos.length})`}
                  </button>
                )}
                {open && (
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {o.exemplos.map((ex, i) => (
                      <li
                        key={i}
                        style={{
                          fontSize: 12,
                          padding: 8,
                          background: 'var(--badge-bg)',
                          borderRadius: 8,
                          color: 'var(--text-body)',
                          lineHeight: 1.4,
                        }}
                      >
                        "{ex}"
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ============ 7) RECOMPRA ============

function Recompra({ data }: { data: RecompraData }) {
  return (
    <section style={{ marginTop: 32 }}>
      <SectionTitle>Recompra</SectionTitle>
      <Card>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            marginBottom: 20,
            padding: '4px 0',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
            Taxa de recompra
          </div>
          <div
            style={{
              fontSize: 38,
              fontWeight: 800,
              color: '#2f7a4a',
              letterSpacing: '-0.03em',
              lineHeight: 1,
            }}
          >
            {formatPct(data.pct_recompra)}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
            {data.clientes_recorrentes.length} de {data.total_clientes_pagantes} clientes pagantes recompraram no período
          </div>
        </div>

        {data.clientes_recorrentes.length === 0 ? (
          <EmptyMsg>Ninguém recomprou no período.</EmptyMsg>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.06 }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Cliente</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>DDD</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Pedidos</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Receita</th>
                  <th
                    style={{ textAlign: 'right', padding: '6px 8px' }}
                    title="Receita total deste comprador ÷ pedidos pagos"
                  >
                    Ticket médio
                  </th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Dias entre 1ª e última</th>
                  <th
                    style={{ textAlign: 'right', padding: '6px 8px', whiteSpace: 'normal', maxWidth: 120, lineHeight: 1.25 }}
                    title="Média do intervalo entre compras consecutivas"
                  >
                    Tempo médio entre compras
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.clientes_recorrentes.map((c) => (
                  <tr key={c.cliente_phone} style={{ borderTop: '1px solid var(--border-fade)' }}>
                    <td style={{ padding: '8px', fontWeight: 600, color: 'var(--heading-ink)' }}>
                      {c.cliente_nome || '(sem nome)'}
                    </td>
                    <td style={{ padding: '8px' }}>{c.ddd}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{c.pedidos_pagos}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, whiteSpace: 'nowrap' }}>{formatBRL(c.receita_total)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatBRL(ticketMedioComprador(c))}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{c.dias_entre_compras}d</td>
                    <td style={{ padding: '8px', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatDuracaoDias(tempoMedioEntreComprasDias(c))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </section>
  );
}

// ============ Helpers ============

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="admin-glass"
      style={{
        background: 'var(--surface-white)',
        borderRadius: 18,
        padding: 16,
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 14,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        color: 'var(--heading-ink)',
        margin: '24px 0 8px',
      }}
    >
      {children}
    </h2>
  );
}

function EmptyMsg({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: 0, padding: 32, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
      {children}
    </p>
  );
}

function Disclaimer({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        padding: '10px 14px',
        borderRadius: 10,
        background: 'rgba(255, 145, 77, 0.08)',
        border: '1px solid rgba(255, 145, 77, 0.25)',
        color: 'var(--amber-deep)',
        fontSize: 12,
        marginBottom: 12,
        lineHeight: 1.4,
      }}
    >
      ℹ️ {children}
    </div>
  );
}

function Callout({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="admin-glass"
      style={{
        padding: 14,
        borderRadius: 14,
        background: 'var(--surface-white)',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
  tooltip,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: 'green' | 'orange' | 'red';
  tooltip?: string;
}) {
  const accentColor =
    accent === 'green' ? '#2f7a4a' : accent === 'orange' ? '#cc6620' : accent === 'red' ? '#a93232' : 'var(--heading-ink)';
  return (
    <div
      style={{
        background: 'var(--badge-bg)',
        border: '1px solid var(--border-soft)',
        borderRadius: 12,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          display: 'flex',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <span>{label}</span>
        {tooltip && (
          <span
            title={tooltip}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 14,
              height: 14,
              borderRadius: 999,
              background: 'var(--surface-white)',
              border: '1px solid var(--border-soft)',
              color: 'var(--text-muted)',
              fontSize: 9,
              fontWeight: 700,
              cursor: 'help',
            }}
          >
            ?
          </span>
        )}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: accentColor,
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{hint}</div>}
    </div>
  );
}

function Th({
  children,
  onClick,
  active,
  dir,
  align = 'right',
}: {
  children: React.ReactNode;
  onClick: () => void;
  active: boolean;
  dir: 'asc' | 'desc';
  align?: 'left' | 'right';
}) {
  return (
    <th
      onClick={onClick}
      style={{
        textAlign: align,
        padding: '6px 8px',
        cursor: 'pointer',
        userSelect: 'none',
        color: active ? 'var(--gold)' : 'inherit',
      }}
    >
      {children}
      {active && <span style={{ marginLeft: 4 }}>{dir === 'asc' ? '↑' : '↓'}</span>}
    </th>
  );
}

function SkeletonGrid() {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: 12,
      }}
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 92,
            background: 'var(--badge-bg)',
            borderRadius: 18,
            border: '1px solid var(--border-soft)',
            opacity: 0.6,
          }}
        />
      ))}
    </div>
  );
}
